import {Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from "@mui/material";
import React, {useEffect, useState} from "react";
import User from "../../../model/User";
import {useResources} from "../../../stores/ResourceProvider";
import Account from "../../../model/Account";
import {ModelUserFilterInput, UserStatus} from "../../../API";
import {getISODateFromDate, isoToLocalDate, numberToMoneyFormat} from "../../../stores/StoreUtilities";
import {globalColors} from "../../../styles/globalStyles";
import Chart from "react-apexcharts";
import LoadingProgress from "../../../components/LoadingProgress";
import {ReferrerIndex, ReferrerOptions} from "../../../stores/UserStore";
import {addMinutes, addMonths, addYears, startOfMonth} from "date-fns";
import cloneDeep from "lodash.clonedeep";
import MonthYearPicker from "../../../components/form/MonthYearPicker";

const defaultOptions = {
  chart: {
    id: "budget-projection",
    type: 'bar',
    stacked: true,
    height: 350,
    width: "100%",
    events: {
      mounted: (chart: any) => {
        chart.windowResizeHandler();
      }
    },
    toolbar: {
      show: false,
      tools: {
        download: false
      }
    }
  },
  title: {
    text: "2025",
    align: "right",
    floating: true,
    margin: 0,
    style: {
      fontSize: "16px",
      fontFamily: "Poppins,sans-serif",
      color: "rgb(31, 92, 167)"
    }
  },
  dataLabels: {
    enabled: false
  },
  legend: {
    position: 'top', // TODO: Figure out why this isn't working
    horizontalAlign: 'left',
    onItemClick: {
      toggleDataSeries: false
    }
  },
  xaxis: {
    categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    axisBorder: {
      show: true
    }
  },
  yaxis: {
    // labels: {
    //   formatter: (value: number) => {
    //     return numberToMoneyFormat(value, 0)
    //   }
    // }
  },
  tooltip: {
    // y: {
    //   formatter: function (value: number) {
    //     return numberToMoneyFormat(value, 0)
    //   }
    // }
  },
  colors: globalColors.categorical,
  responsive: [{
    breakpoint: undefined,
    options: {}
  }]
}

class Referrer {
  name: string
  data: number[]
  total: number

  constructor (name: string, size: number) {
    this.name = name
    this.data = new Array<number>(size).fill(0)
    this.total = 0
  }
}

type SeriesItem = {
  name: string
  group?: string
  data: number[]
}

const ReferrersReport = ({width}: {width: number}) => {
  const [account, setAccount] = useState<Account | undefined>()
  const [users, setUsers] = useState<User[]>([])
  const [referrers, setReferrers] = useState<Referrer[]>([])
  const [otherReferrers, setOtherReferrers] = useState<Referrer[]>([])
  const [options, setOptions] = useState<any>()
  const [series, setSeries] = useState<any>()
  const [startDate, setStartDate] = useState<Date>(addMonths(startOfMonth(new Date()), -11))
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const {userStore, accountStore, notify} = useResources()

  useEffect(() => {
    if (userStore.isLoading === false && userStore.user && userStore.user.account) {
      setAccount(userStore.user.account)
      setUsers([])
      if (!isLoading) {
        loadUsers(userStore.user.account)
      }
    }
  }, [userStore.isLoading, startDate])

  useEffect(() => {
    calculateData()
  }, [users])

  const loadUsers = async (account: Account) => {
    try {
      setIsLoading(true)
      let endDate = addMinutes(addYears(startDate, 1), -1)
      let filter : ModelUserFilterInput | undefined =
        {
          startDate: {between: [getISODateFromDate(startDate), getISODateFromDate(endDate)]}
        }
      let list = await accountStore.listUsersByAccount(account.id, filter)
      setUsers(list)
    } catch (err: any) {
      console.error("loadUsers error", err)
      notify.show("error", err.message)
    } finally {
      setIsLoading(false)
    }
  }

  const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  const calculateData = () => {
    const referrerList: Referrer[] = []
    referrerList.push(new Referrer("Direct (no-referrer)", 12))
    referrerList.push(new Referrer("Other", 12))
    const otherList: Referrer[] = []

    const startMonth = startDate.getMonth()

    users.forEach((user: User) => {
      let signupMonth = isoToLocalDate(user.startDate).getMonth()
      if (signupMonth < startMonth) {
        signupMonth += 12
      }
      signupMonth -= startMonth

      let referrer = user.referrer ? user.referrer.trim() : undefined
      let referrerOption: string | undefined
      if (referrer) {
        referrerOption = ReferrerOptions.find((r: string) => r === referrer)
        if (!referrerOption) {
          if (referrer.search(/^ai$|chatgpt|gemini|grok|perplexity/i) >= 0) {
            referrerOption = ReferrerOptions[ReferrerIndex.AI]
          } else if (referrer.search(/facebook|face book|fb/i) >= 0) {
            referrerOption = ReferrerOptions[ReferrerIndex.Facebook]
          } else if (referrer.search(/email|book/i) >= 0) {
            referrerOption = ReferrerOptions[ReferrerIndex.EmailBook]
          } else if (referrer.search(/friend|family|mom|dad|brother|sister|work|relative/i) >= 0) {
            referrerOption = ReferrerOptions[ReferrerIndex.FriendFamily]
          } else if (referrer.search(/google|bing|web|search/i) >= 0) {
            referrerOption = ReferrerOptions[ReferrerIndex.Google]
          } else if (referrer.search(/instagram|^ig$|insta/i) >= 0) {
            referrerOption = ReferrerOptions[ReferrerIndex.Instagram]
          } else if (referrer.search(/sound retirement/i) >= 0) {
            referrerOption = ReferrerOptions[ReferrerIndex.PodcastSRR]
          } else if (referrer.search(/parker|jason/i) >= 0) {
            referrerOption = ReferrerOptions[ReferrerIndex.ParkerFinancial]
          } else if (referrer.search(/podcast|pod|spotify|apple/i) >= 0) {
            referrerOption = ReferrerOptions[ReferrerIndex.PodcastOther]
          } else if (referrer.search(/reddit|redit/i) >= 0) {
            referrerOption = ReferrerOptions[ReferrerIndex.Reddit]
          } else if (referrer.search(/tiktok/i) >= 0) {
            referrerOption = ReferrerOptions[ReferrerIndex.TikTok]
          } else if (referrer.search(/bing|web|search|internet|online/i) >= 0) {
            referrerOption = ReferrerOptions[ReferrerIndex.WebSearch]
          } else if (referrer.search(/youtube|you tube|utube|video/i) >= 0) {
            referrerOption = ReferrerOptions[ReferrerIndex.YouTube]
          } else if (referrer.search(/twitter|^x$/i) >= 0) {
            referrerOption = ReferrerOptions[ReferrerIndex.XTwitter]
          } else {
            // referrerOption = user.referrer
            referrerOption = ReferrerOptions[ReferrerIndex.Other]

            const otherOption = referrer.toLowerCase()
            let otherListItem = otherList.find((r: Referrer) => r.name.toLowerCase() === otherOption)
            if (!otherListItem) {
              otherListItem = new Referrer(otherOption, 12)
              otherList.push(otherListItem)
            }
            otherListItem.data[signupMonth]++
            otherListItem.total++
          }
        } else if (referrerOption === ReferrerOptions[ReferrerIndex.Other]) {
          if (user.referrerDetails) {
            const otherOption = user.referrerDetails.trim().toLowerCase()
            let otherListItem = otherList.find((r: Referrer) => r.name.toLowerCase() === otherOption)
            if (!otherListItem) {
              otherListItem = new Referrer(otherOption, 12)
              otherList.push(otherListItem)
            }
            otherListItem.data[signupMonth]++
            otherListItem.total++
          }
        }
      } else {
        referrerOption = ReferrerOptions[ReferrerIndex.Direct]
      }
      const referrerOptionLC = referrerOption.toLowerCase()
      let listItem = referrerList.find((r: Referrer) => r.name.toLowerCase() === referrerOptionLC)
      if (!listItem) {
        listItem = new Referrer(referrerOption, 12)
        referrerList.push(listItem)
      }
      listItem.data[signupMonth]++
      listItem.total++
    })

    // Sort the list by name
    // referrerList.sort((a: Referrer, b: Referrer) => a.name.localeCompare(b.name))

    // Sort the list by total
    referrerList.sort((a: Referrer, b: Referrer) => b.total - a.total)
    otherList.sort((a: Referrer, b: Referrer) => b.total - a.total)
    // Limit chart to top 10
    const topReferrers = referrerList.slice(0, 10)

    const series: SeriesItem[] = []
    topReferrers.forEach((referrer: Referrer) => {
      series.push({
        name: referrer.name,
        group: (referrer.name === ReferrerOptions[ReferrerIndex.Google] || referrer.name === ReferrerOptions[ReferrerIndex.WebSearch]) ? "Search" : referrer.name,
        data: referrer.data
      })
    })

    setReferrers(referrerList)
    setOtherReferrers(otherList)
    setSeries(series)

    const options = cloneDeep(defaultOptions)
    // Adjust xAxis categories, if not starting in Jan
    if (startMonth > 0) {
      const rotated = [...monthLabels.slice(startMonth), ...monthLabels.slice(0, startMonth)]
      options.xaxis.categories = rotated
    }
    setOptions(options)
  }

  const renderTable = (title: string, referrers: Referrer[]) => {
    let months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

    const startMonth = startDate.getMonth()
    if (startMonth > 0) {
      months = [...months.slice(startMonth), ...months.slice(0, startMonth)]
    }

    return (
      <Box display="flex" flexGrow={1} flexDirection="column" mt={2}>
        <Box display="flex" flexGrow={1} justifyContent="flex-start" alignItems="center" maxHeight={40} mb={1}>
          <Typography variant="h3" color="primary" sx={{mr:2}}>{title}</Typography>
          <MonthYearPicker value={startDate}
                           onChange={(value: Date) => setStartDate(value)} />
        </Box>
        <TableContainer component={Paper} sx={{paddingTop:0}}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="20%">Referrer</TableCell>
                {months.map((month: string) =>
                  <TableCell align="right">{month}</TableCell>
                )}
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {referrers.map((referrer: Referrer, index: number) =>
                <TableRow key={`row${index}`}>
                  <TableCell>{referrer.name}</TableCell>
                  {referrer.data.map((value: number) =>
                    <TableCell align="right">{value}</TableCell>
                    )}
                 <TableCell align="right"><b>{referrer.total}</b></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )
  }

  if (isLoading) {
    return (
      <TableContainer component={Paper} sx={{paddingTop:1, overflowY:"hidden", minHeight:"350px", width:width-32}}>
        <Box display="flex" justifyContent="center" alignItems="center">
          <LoadingProgress marginTop="150px"/>
        </Box>
      </TableContainer>
    )
  }

  if (options && series) {
    return (
      <Box display="flex" flexGrow={1} flexDirection="column" width={width-32} overflow="scroll">
      <TableContainer component={Paper} sx={{paddingTop:1, overflowY:"hidden", minHeight:"350px"}}>
          <Chart
            options={options}
            series={series}
            type="bar"
            height="350"
            width={`${width-32}px`}
          />
        </TableContainer>

        {renderTable("Referrers", referrers)}
        {renderTable("Other Referrers", otherReferrers)}
      </Box>
    )
  } else {
    return null
  }
}

export default ReferrersReport